import { Button, Input, Select } from '@aidkitorg/component-library';

export type SupportTableFilterOptions = {
  lastMessageStart: string;
  lastMessageEnd: string;
  caseResolvedStart: string;
  caseResolvedEnd: string;
  tags: string;
  contactMethod: string;
  assignedTo: string;
  extraColumns: Record<string, string>;
}

export type TagOption = { value: string; label: string };
export type UserOption = { value: string; label: string };
export type ExtraColumnFilterOption = { value: string; label: string };
export type ExtraColumnFilterConfig = {
  targetField: string;
  label: string;
  options: ExtraColumnFilterOption[];
};

type SupportTableFiltersProps = {
  table: any;
  filters: SupportTableFilterOptions;
  setFilters: (filters: SupportTableFilterOptions) => void;
  filter: string;
  L: any;
  tagOptions: TagOption[];
  userOptions: UserOption[];
  extraColumnFilters?: ExtraColumnFilterConfig[];
};

export const SupportTableFilters: React.FC<SupportTableFiltersProps> = ({ table, filters, setFilters, filter, L, tagOptions, userOptions, extraColumnFilters = [] }) => {
  const applyFilters = () => {
    const lastMessageColumn = table.getColumn('max');
    const tagsColumn = table.getColumn('str_tags');
    const contactMethodColumn = table.getColumn('str_contact_methods');
    const assignedToColumn = table.getColumn('assigned_to_name');

    // Apply date range filter for last messaged
    lastMessageColumn?.setFilterValue(
      filters.lastMessageStart || filters.lastMessageEnd
        ? { start: filters.lastMessageStart, end: filters.lastMessageEnd }
        : undefined
    );

    // Apply date range filter for case resolved (only if in resolved view)
    if (filter === 'resolved') {
      const caseResolvedColumn = table.getColumn('case_resolved');
      caseResolvedColumn?.setFilterValue(
        filters.caseResolvedStart || filters.caseResolvedEnd
          ? { start: filters.caseResolvedStart, end: filters.caseResolvedEnd }
          : undefined
      );
    }

    // Apply other filters
    tagsColumn?.setFilterValue(filters.tags);
    contactMethodColumn?.setFilterValue(filters.contactMethod);
    assignedToColumn?.setFilterValue(filters.assignedTo);

    // Apply extra column filters
    for (const extraFilter of extraColumnFilters) {
      const column = table.getColumn(`extra_${extraFilter.targetField}`);
      column?.setFilterValue(filters.extraColumns[extraFilter.targetField] || '');
    }
  };

  const resetFilters = () => {
    setFilters({
      lastMessageStart: '',
      lastMessageEnd: '',
      caseResolvedStart: '',
      caseResolvedEnd: '',
      tags: '',
      contactMethod: '',
      assignedTo: '',
      extraColumns: {}
    });
    table.resetColumnFilters();
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap gap-4">
        {/* Last Message Date Range */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {L.support.fields.max}
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={filters.lastMessageStart}
              onChange={(e) => setFilters({ ...filters, lastMessageStart: e.target.value })}
              className="flex-1"
              label={L.support.fields.start_date}
              showLabel={false}
            />
            <span className="text-gray-500">to</span>
            <Input
              type="date"
              value={filters.lastMessageEnd}
              onChange={(e) => setFilters({ ...filters, lastMessageEnd: e.target.value })}
              className="flex-1"
              label={L.support.fields.end_date}
              showLabel={false}
            />
          </div>
        </div>

        {/* Case Resolved Date Range (only shown in resolved view) */}
        {filter === 'resolved' && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {L.support.fields.case_resolved}
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={filters.caseResolvedStart}
                onChange={(e) => setFilters({ ...filters, caseResolvedStart: e.target.value })}
                className="flex-1"
                label={L.support.fields.start_date}
                showLabel={false}
              />
              <span className="text-gray-500">to</span>
              <Input
                type="date"
                value={filters.caseResolvedEnd}
                onChange={(e) => setFilters({ ...filters, caseResolvedEnd: e.target.value })}
                className="flex-1"
                label={L.support.fields.end_date}
                showLabel={false}
              />
            </div>
          </div>
        )}

        {/* Tags filter */}
        {tagOptions?.length && <div className="space-y-2">
          <Select
            description="Select a tag to filter by"
            value={tagOptions.find(opt => opt.value === filters.tags) || tagOptions[0]}
            onChange={(option) => setFilters({ ...filters, tags: option.value })}
            options={tagOptions}
            label={L.support.fields.tags}
          />
        </div>}

        {/* Contact Method filter */}
        <div className="space-y-2">
          <Select
            description="Select a contact method to filter by"
            value={{ value: filters.contactMethod, label: filters.contactMethod || L.support.filters.all_contact_methods }}
            onChange={(option) => setFilters({ ...filters, contactMethod: option.value })}
            options={[
              { value: "", label: L.support.filters.all_contact_methods },
              { value: "call", label: "Call" },
              { value: "email", label: "Email" },
              { value: "sms", label: "SMS" },
              { value: "whatsapp", label: "WhatsApp" }
            ]}
            label={L.support.fields.contact_methods}
          />
        </div>

        {/* Assigned To filter */}
        <div className="space-y-2">

          <Select
            description="Select an assignee to filter by"
            value={userOptions.find(opt => opt.value === filters.assignedTo) || userOptions[0]}
            onChange={(option) => setFilters({ ...filters, assignedTo: option.value })}
            options={userOptions}
            label={L.support.fields.assigned_to_name}
          />
        </div>

        {/* Extra column filters */}
        {extraColumnFilters.map(extraFilter => (
          <div key={extraFilter.targetField} className="space-y-2">
            <Select
              description={`Select a ${extraFilter.label.toLowerCase()} to filter by`}
              value={extraFilter.options.find(opt => opt.value === filters.extraColumns[extraFilter.targetField]) || extraFilter.options[0]}
              onChange={(option) => setFilters({
                ...filters,
                extraColumns: {
                  ...filters.extraColumns,
                  [extraFilter.targetField]: option?.value || ''
                }
              })}
              options={extraFilter.options}
              label={extraFilter.label}
            />
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="secondary" onClick={resetFilters}>
          {L.support.filters.reset}
        </Button>
        <Button onClick={applyFilters}>
          {L.support.filters.apply}
        </Button>
      </div>
    </div>
  );
};
